import { assert } from '@ember/debug';
import { captureException } from '@sentry/ember';
import FlagsClient from '@square/ember-features/utils/clients/flags';
import { formatToLaunchdarklyKeyIfNeeded } from '@square/ember-features/utils/feature-flag-name-format';
import { GetFlagRequest, GetFlagsRequest } from '@square/ember-features/protos/squareup/feature/relay/flags/message/get_flags';
import { Token } from '@square/ember-features/protos/squareup/feature/relay/common/token';
import { Attribute } from '@square/ember-features/protos/squareup/feature/relay/common/attribute';
import { UserAttributes } from '@square/ember-features/protos/squareup/feature/relay/common/user_attributes';
import { DataType } from '@square/ember-features/protos/squareup/feature/relay/common/data_type';
import { Status } from '@square/ember-features/protos/squareup/feature/relay/flags/message/status';
export const flagTypeField = {
  [DataType.BOOLEAN]: 'boolValue',
  [DataType.DOUBLE]: 'doubleValue',
  [DataType.INTEGER]: 'intValue',
  [DataType.STRING]: 'stringValue',
  [DataType.NOT_SPECIFIED]: 'boolValue',
  [DataType.JSON]: 'stringValue'
};
export function fetchFlags(flags, merchantToken, attributes) {
  assert('there should be at least one feature flag passed in', flags && flags.length > 0);
  const flagRequests = flags.map(flag => {
    // If a flag name matches a pattern of `group/feature_name`, then it needs to be converted to
    // standard LD key convention of `group-feature-name`, otherwise just use the defined name and don't
    // replace undescores, since those are valid in LD flag keys and LD keys can not contain '/' characters.
    const ldFlagKey = formatToLaunchdarklyKeyIfNeeded(flag.flagName);
    return GetFlagRequest.create({
      flagName: ldFlagKey,
      project: flag.project,
      flagType: flag.type || DataType.BOOLEAN
    });
  });
  const requestParams = {
    flagRequests,
    userToken: Token.create({
      token: merchantToken,
      type: Token.Type.MERCHANT
    })
  };
  if (attributes) {
    // Add attributes to request
    requestParams.userAttributes = UserAttributes.create({
      userAttributes: Object.entries(attributes).map(([attribute, value]) => {
        if (attribute === 'timestamp') {
          return Attribute.create({
            name: attribute,
            intValue: Number.parseInt(value, 10)
          });
        } else if (typeof value === 'boolean') {
          return Attribute.create({
            name: attribute,
            boolValue: value
          });
        } else {
          return Attribute.create({
            name: attribute,
            stringValue: value
          });
        }
      })
    });
  }
  const request = GetFlagsRequest.create(requestParams);
  return FlagsClient.getFlags(request);
}
export function parseFlagValues(response) {
  const flags = {};

  // Convert GetFlagsResponse into FlagValuesObject to be used for setup.
  // Excludes MISSING_USER_TOKEN responses for when the multipass token is expired.
  response.flagResponses.filter(flagResponse => flagResponse.status !== Status.MISSING_USER_TOKEN).forEach(flagResponse => {
    const flagType = flagResponse.flagType || DataType.BOOLEAN;
    // Return LD flag keys, do not mangle the returned keys.
    flags[flagResponse.flagName] = flagResponse[flagTypeField[flagType]];
    if (flagResponse.flagType === DataType.JSON) {
      try {
        flags[flagResponse.flagName] = JSON.parse(flags[flagResponse.flagName]);
      } catch {
        flags[flagResponse.flagName] = undefined;
        captureException(new Error(`Failed to parse "${flagResponse.flagName}" JSON feature flag value`));
      }
    }
  });
  return flags;
}
export function parseFlagTypes(response) {
  const flagTypes = {};
  response.flagResponses.filter(flagResponse => flagResponse.status !== Status.MISSING_USER_TOKEN).forEach(flagResponse => {
    const flagType = flagResponse.flagType || DataType.BOOLEAN;
    // Return LD flag keys, do not mangle the returned keys.
    flagTypes[flagResponse.flagName] = flagType;
  });
  return flagTypes;
}